import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Mail, Lock, ArrowRight, ArrowLeft, ChefHat } from 'lucide-react';
import { Button } from '../components/common';

function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}

export default function LoginPage() {
  const { signIn, signInWithApple } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleAppleSignIn() {
    setError('');
    setLoading(true);
    try {
      await signInWithApple();
      navigate('/');
    } catch (err) {
      const msg = err.message || '';
      // Error 1001 = user cancelled the Apple Sign In sheet — not an error.
      if (msg.includes('1001') || msg.toLowerCase().includes('cancel')) {
        // Silently ignore cancellation
      } else {
        setError(msg ? `Apple Sign In failed: ${msg}` : 'Apple Sign In failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await signIn(email, password);
      if (result.isSignedIn) {
        navigate('/');
      } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
        navigate('/auth/verify', { state: { email } });
      }
    } catch (err) {
      setError(err.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-night-bg safe-area-top safe-area-bottom">
      <button
        type="button"
        onClick={() => navigate(-1)}
        aria-label="Go back"
        className="fixed top-3 left-3 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white/70 dark:bg-night-surface/70 backdrop-blur text-gray-700 dark:text-night-text shadow-sm safe-area-top"
        style={{ top: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
      >
        <ArrowLeft size={22} />
      </button>

      <div className="flex flex-col items-center justify-start min-h-screen px-4 py-12 max-w-md mx-auto">
        <div className="text-center mb-8 mt-12">
          <div className="inline-flex w-14 h-14 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 mb-3" aria-hidden="true">
            <ChefHat size={28} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-night-text">Welcome back</h1>
          <p className="text-sm text-gray-500 dark:text-night-muted mt-1">Sign in to your Cooksmith account</p>
        </div>

        <div className="w-full bg-white dark:bg-night-surface rounded-2xl shadow-sm border border-gray-100 dark:border-night-card p-5 space-y-4">
          {error && (
            <div className="text-sm text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={handleAppleSignIn}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 min-h-[44px] rounded-lg bg-black text-white font-medium hover:bg-gray-800 active:bg-gray-900 disabled:opacity-50"
          >
            <AppleIcon />
            Sign in with Apple
          </button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200 dark:border-night-card" /></div>
            <div className="relative flex justify-center"><span className="px-3 text-xs text-gray-500 dark:text-night-muted bg-white dark:bg-night-surface uppercase tracking-wide">or</span></div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 dark:text-night-text mb-1">Email</span>
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-night-muted" aria-hidden="true" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  autoComplete="email"
                  className="w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-night-card rounded-lg bg-white dark:bg-night-card text-sm text-gray-900 dark:text-night-text placeholder-gray-400 dark:placeholder-night-muted focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </label>

            <label className="block">
              <span className="block text-sm font-medium text-gray-700 dark:text-night-text mb-1">Password</span>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-night-muted" aria-hidden="true" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                  className="w-full pl-10 pr-10 py-2.5 border border-gray-300 dark:border-night-card rounded-lg bg-white dark:bg-night-card text-sm text-gray-900 dark:text-night-text placeholder-gray-400 dark:placeholder-night-muted focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded text-gray-500 dark:text-night-muted"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </label>

            <div className="flex justify-end">
              <Link to="/auth/forgot-password" className="text-sm text-primary-600 dark:text-primary-400 hover:underline">
                Forgot password?
              </Link>
            </div>

            <Button type="submit" disabled={loading} className="w-full">
              {loading ? 'Signing in…' : 'Sign In'}
              {!loading && <ArrowRight size={18} aria-hidden="true" />}
            </Button>
          </form>
        </div>

        <p className="text-sm text-gray-500 dark:text-night-muted mt-6">
          Don't have an account?{' '}
          <Link to="/auth/signup" className="text-primary-600 dark:text-primary-400 font-medium hover:underline">
            Create one
          </Link>
        </p>
      </div>
    </div>
  );
}
